import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, X } from 'lucide-react';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { Field, inputClass, labelClass } from '../../../../../components/ui/Field';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { fetchProductOptions } from '../../../../../features/catalog/catalogThunks';
import { productOptionLabel } from '../../../../../features/catalog/catalogUtils';
import { createReceipt, updateReceipt } from '../../../../../features/receipts/receiptsThunks';
import { EMPTY_RECEIPT_LINE } from '../../../../../features/receipts/receiptsConstants';
import {
  buildDeclarePayload,
  buildDeclareUpdatePayload,
  declareFormFrom,
  declareProblem,
  isReceivableProduct,
} from '../../../../../features/receipts/receiptsUtils';

/**
 * Declares what is coming: creates a receiving order, or edits a pending one.
 *
 * It does not move stock — it is an expectation. Lot and expiry are optional
 * here even for a product that controls them, because whoever declares often
 * does not know them yet; the count is what demands them. No location either:
 * where the load lands is chosen with the box in hand.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object|null} props.warehouse - Where a new order will arrive.
 * @param {object|null} props.receipt - The pending order being edited; `null` for a new one.
 * @param {Array<{value: number, label: string}>} props.clientOptions - The
 *   clients operating in that warehouse. With a single one it is not asked.
 * @param {(message: string) => void} props.onSaved - Told what happened, in a sentence.
 */
export const DeclareReceiptModal = ({ isOpen, onClose, warehouse, receipt, clientOptions, onSaved }) => {
  const dispatch = useDispatch();
  const saving = useSelector((state) => state.receipts.saving);
  const { options: products, optionsCompanyId, loadingOptions } = useSelector(
    (state) => state.catalog
  );

  const [form, setForm] = useState(() => declareFormFrom(receipt));
  const [companyId, setCompanyId] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const isEditing = Boolean(receipt);
  const onlyClientId = clientOptions.length === 1 ? clientOptions[0].value : null;

  // Seeded on each opening, from the order that asked for it.
  useEffect(() => {
    if (!isOpen) return;
    setForm(declareFormFrom(receipt));
    setCompanyId(receipt ? receipt.company : onlyClientId);
    setSaveError(null);
  }, [isOpen, receipt, onlyClientId]);

  // The whole catalog of the client, not its first page: it is a picker.
  useEffect(() => {
    if (isOpen && companyId) dispatch(fetchProductOptions(companyId));
  }, [isOpen, companyId, dispatch]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const setLine = (index, patch) =>
    set({ lines: form.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)) });

  // What the order already has stays readable whatever the catalog brings: a
  // product taken out of service is no longer offered, and it is still a line.
  // A virtual kit never arrives as itself: its components do.
  const offered = optionsCompanyId === companyId ? products.filter(isReceivableProduct) : [];
  const offeredIds = new Set(offered.map((product) => product.id));
  const productChoices = [
    ...offered.map((product) => ({ value: product.id, label: productOptionLabel(product) })),
    ...(receipt?.lines || [])
      .filter((line) => !offeredIds.has(line.product))
      .map((line) => ({
        value: line.product,
        label: `${line.product_sku} — ${line.product_name}`,
      })),
  ];

  const warehouseName = isEditing ? receipt.warehouse_name : warehouse?.name;
  const problem = declareProblem(form, companyId);

  const handleSave = async () => {
    setSaveError(null);

    const action = await dispatch(
      isEditing
        ? updateReceipt({ receiptId: receipt.id, payload: buildDeclareUpdatePayload(form) })
        : createReceipt(buildDeclarePayload(form, { warehouseId: warehouse.id, companyId }))
    );

    if (action.meta.requestStatus !== 'fulfilled') {
      setSaveError(action.payload);
      return;
    }

    onSaved(
      isEditing
        ? `${receipt.code} actualizada.`
        : `Se creó ${action.payload.code}. El stock entra cuando se recepcione.`
    );
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={isEditing ? `Editar ${receipt.code}` : 'Nueva orden de recepción'}
      testId="receipt-declare-modal"
    >
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6">
        {warehouseName} · se declara lo esperado; el stock entra al recepcionar
      </p>

      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
        {saveError && (
          <div
            data-testid="receipt-declare-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm"
          >
            {saveError}
          </div>
        )}

        {/* Whose goods they are is declared, not worked out from the warehouse:
            several companies share a distribution centre. Once the order
            exists, it does not change hands. */}
        {clientOptions.length > 1 && (
          <FilterableSelect
            variant="form"
            testId="receipt-client"
            label="Cliente"
            value={companyId}
            // Another client is another catalog: its lines start over.
            onChange={(value) => {
              setCompanyId(value);
              set({ lines: [{ ...EMPTY_RECEIPT_LINE }] });
            }}
            options={clientOptions}
            placeholder="¿De quién es la mercadería que llega?"
            searchPlaceholder="Buscar cliente..."
            disabled={saving || isEditing}
          />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Proveedor" htmlFor="receipt-supplier" hint="Quien trae la carga.">
            <input
              id="receipt-supplier"
              type="text"
              data-testid="receipt-supplier"
              value={form.supplier}
              onChange={(event) => set({ supplier: event.target.value })}
              disabled={saving}
              className={inputClass}
            />
          </Field>
          <Field
            label="Guía / factura / OC"
            htmlFor="receipt-reference"
            hint="El número externo de la entrega."
          >
            <input
              id="receipt-reference"
              type="text"
              data-testid="receipt-reference"
              value={form.reference}
              onChange={(event) => set({ reference: event.target.value })}
              disabled={saving}
              className={inputClass}
            />
          </Field>
        </div>

        <div>
          <p className={labelClass}>Líneas esperadas</p>
          {!companyId && (
            <p className="text-sm text-text-muted mb-2">Elige el cliente para ver sus productos.</p>
          )}

          <div className="space-y-3">
            {form.lines.map((line, index) => (
              <div
                key={index}
                data-testid="receipt-line"
                className="rounded-xl border border-border bg-ninja-gray/40 dark:bg-[#111] p-3 space-y-3"
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <FilterableSelect
                      variant="form"
                      testId={`receipt-line-product-${index}`}
                      value={line.productId}
                      onChange={(productId) => setLine(index, { productId })}
                      options={productChoices}
                      placeholder={loadingOptions ? 'Cargando productos...' : 'Seleccionar SKU...'}
                      searchPlaceholder="Buscar por SKU o nombre..."
                      disabled={saving || !companyId}
                    />
                  </div>
                  <Button
                    variant="subtle"
                    iconOnly
                    icon={X}
                    title="Quitar línea"
                    aria-label={`Quitar la línea ${index + 1}`}
                    className="hover:text-danger"
                    disabled={saving || form.lines.length === 1}
                    onClick={() => set({ lines: form.lines.filter((_, i) => i !== index) })}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="Cantidad esperada" htmlFor={`receipt-line-expected-${index}`}>
                    <input
                      id={`receipt-line-expected-${index}`}
                      type="number"
                      min="0"
                      step="any"
                      data-testid={`receipt-line-expected-${index}`}
                      value={line.expected}
                      onChange={(event) => setLine(index, { expected: event.target.value })}
                      disabled={saving}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Lote (opcional)" htmlFor={`receipt-line-lot-${index}`}>
                    <input
                      id={`receipt-line-lot-${index}`}
                      type="text"
                      data-testid={`receipt-line-lot-${index}`}
                      value={line.lot}
                      onChange={(event) => setLine(index, { lot: event.target.value })}
                      disabled={saving}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Vence (opcional)" htmlFor={`receipt-line-expiry-${index}`}>
                    <input
                      id={`receipt-line-expiry-${index}`}
                      type="date"
                      data-testid={`receipt-line-expiry-${index}`}
                      value={line.expiry_date}
                      onChange={(event) => setLine(index, { expiry_date: event.target.value })}
                      disabled={saving}
                      className={inputClass}
                    />
                  </Field>
                </div>
              </div>
            ))}
          </div>

          <Button
            variant="ghost"
            size="sm"
            icon={Plus}
            className="mt-3"
            disabled={saving || !companyId}
            onClick={() => set({ lines: [...form.lines, { ...EMPTY_RECEIPT_LINE }] })}
            data-testid="receipt-line-add"
          >
            Agregar SKU
          </Button>
        </div>

        <Field label="Notas (opcional)" htmlFor="receipt-notes">
          <textarea
            id="receipt-notes"
            rows={2}
            data-testid="receipt-notes"
            value={form.notes}
            onChange={(event) => set({ notes: event.target.value })}
            disabled={saving}
            placeholder="Observaciones: daños, faltantes, etc."
            className={inputClass}
          />
        </Field>
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        {/* Disabled, it says what is missing. */}
        <Button
          variant="primary"
          loading={saving}
          loadingLabel="Guardando..."
          disabled={Boolean(problem)}
          onClick={handleSave}
          data-testid="receipt-declare-save"
        >
          {problem || (isEditing ? 'Guardar cambios' : 'Crear orden')}
        </Button>
      </div>
    </Modal>
  );
};
